/**
 * OperationTypes — Platform Admin страница справочника тех. операций (AK-238).
 *
 * Тонкий wrapper над `ReferenceCatalogList` + `OperationTypeEditorDrawer`,
 * переиспользует reference-CRUD эталон AK-231/232/234. Delete = архивирование
 * (soft-delete через archived_at). Категория — фиксированный enum
 * (OPERATION_CATEGORIES), НЕ мини-справочник (как у сорняков).
 *
 * Список приходит плоским массивом (не PagedResponse) и без серверного
 * `search` — текстовый поиск по коду/названию делаем клиентски, фильтр по
 * категории — серверный.
 */
import { useMemo, useState } from 'react';
import { Archive, MoreHorizontal, Pencil } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PlatformAdminLayout } from '@/layouts/PlatformAdminLayout';
import {
  ReferenceCatalogList,
  type ReferenceColumn,
} from '@/components/reference/ReferenceCatalogList';
import { OperationTypeEditorDrawer } from './OperationTypeEditorDrawer';
import {
  useArchiveOperationType,
  useCreateOperationType,
  useOperationTypes,
  useUpdateOperationType,
} from './hooks/useCatalogQueries';
import {
  OPERATION_CATEGORIES,
  operationCategoryLabel,
  type OperationTypeCreatePayload,
  type OperationTypeListItem,
} from './types';
import { toast } from '@/lib/toast';

function plural(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m100 >= 11 && m100 <= 14) return 'записей';
  if (m10 === 1) return 'запись';
  if (m10 >= 2 && m10 <= 4) return 'записи';
  return 'записей';
}

export function PlatformAdminOperationTypesPage() {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<OperationTypeListItem | null>(null);

  const opsQ = useOperationTypes(
    categoryFilter === 'all' ? undefined : categoryFilter,
  );
  const createMut = useCreateOperationType();
  const updateMut = useUpdateOperationType();
  const archiveMut = useArchiveOperationType();

  // Серверный фильтр — по категории; текстовый поиск — клиентский
  // (endpoint /operation-types не поддерживает ?search).
  const items = useMemo(() => {
    const all = opsQ.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (o) =>
        o.code.toLowerCase().includes(q) ||
        (o.name_translations.ru ?? '').toLowerCase().includes(q),
    );
  }, [opsQ.data, search]);

  const total = items.length;

  const handleCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };
  const handleEdit = (o: OperationTypeListItem) => {
    setEditing(o);
    setDrawerOpen(true);
  };

  const handleSubmit = async (payload: OperationTypeCreatePayload) => {
    if (editing) {
      // PATCH не принимает code/country_code — отрезаем их.
      const { code: _code, country_code: _cc, ...rest } = payload;
      void _code;
      void _cc;
      await updateMut.mutateAsync({ id: editing.id, payload: rest });
      toast.success(`Операция «${payload.name_translations.ru}» сохранена`);
    } else {
      await createMut.mutateAsync(payload);
      toast.success(`Операция «${payload.name_translations.ru}» создана`);
    }
  };

  const handleArchive = async (o: OperationTypeListItem) => {
    if (!window.confirm(`Архивировать «${o.name_translations.ru ?? o.code}»?`))
      return;
    try {
      await archiveMut.mutateAsync(o.id);
      toast.success('Архивировано.');
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const columns: ReferenceColumn<OperationTypeListItem>[] = [
    {
      key: 'name',
      header: 'Название (RU)',
      render: (o) => (
        <button
          type="button"
          onClick={() => handleEdit(o)}
          className="text-left font-medium text-foreground hover:text-primary"
        >
          {o.name_translations.ru ?? o.code}
        </button>
      ),
    },
    {
      key: 'code',
      header: 'Код',
      width: '170px',
      render: (o) => (
        <span className="font-mono text-xs text-muted-foreground">{o.code}</span>
      ),
    },
    {
      key: 'category',
      header: 'Категория',
      width: '170px',
      render: (o) =>
        o.category ? (
          <Badge variant="secondary">{operationCategoryLabel(o.category)}</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      key: 'flags',
      header: 'Посевное окно',
      width: '160px',
      render: (o) => (
        <div className="flex flex-wrap gap-1">
          {o.is_sowing_start && (
            <Badge
              variant="outline"
              className="border-accent-brand/40 text-accent-brand"
            >
              начинает
            </Badge>
          )}
          {o.is_sowing_close && (
            <Badge variant="outline">закрывает</Badge>
          )}
          {!o.is_sowing_start && !o.is_sowing_close && (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '50px',
      align: 'right',
      render: (o) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
              aria-label={`Действия для ${o.name_translations.ru ?? o.code}`}
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onSelect={() => handleEdit(o)}>
              <Pencil className="h-4 w-4" />
              Редактировать
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => void handleArchive(o)}>
              <Archive className="h-4 w-4" />
              Архивировать
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <PlatformAdminLayout>
      <ReferenceCatalogList<OperationTypeListItem>
        title="Тех. операции"
        subtitle={`Системный справочник · read-only baseline для всех хозяйств · ${total} ${plural(total)}`}
        createLabel="Создать операцию"
        onCreate={handleCreate}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Поиск по названию или коду…"
        items={items}
        isLoading={opsQ.isLoading}
        isError={opsQ.isError}
        error={opsQ.error ?? undefined}
        onRetry={() => opsQ.refetch()}
        getRowKey={(o) => o.id}
        columns={columns}
        emptyTitle="Пока нет ни одной операции"
        emptyHint="Создайте первую запись справочника тех. операций — код, категория и динамическая форма станут доступны всем хозяйствам."
        toolbarSlot={
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все категории</SelectItem>
              {OPERATION_CATEGORIES.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      <OperationTypeEditorDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        mode={editing ? 'edit' : 'create'}
        operationType={editing}
        onSubmit={handleSubmit}
      />
    </PlatformAdminLayout>
  );
}

export default PlatformAdminOperationTypesPage;
